import { format, startOfMonth } from 'date-fns';
import { jobsDateRangeForApi, jobsDateRangeForStatsApi } from './jobs';
import {
    maintenancePartsTotalCents,
    replacementPartsCount,
} from './printerMaintenances';
import { sortPrintersByName } from './printers';

export const PRINTERS_OVERVIEW_PERIOD_MODES = [
    { value: 'all_time', label: 'Todo o tempo' },
    { value: 'panel_period', label: 'Filtro da lista' },
    { value: 'custom', label: 'Período específico' },
];

export function resolvePrintersOverviewPeriod({ mode, appliedPeriod, customFrom, customTo }) {
    if (mode === 'all_time') {
        return {
            finishedFrom: '',
            finishedTo: '',
            period: { from: '', to: '' },
            error: null,
        };
    }

    if (mode === 'panel_period') {
        const range = jobsDateRangeForApi(appliedPeriod?.from, appliedPeriod?.to);
        if (range.error) {
            return { error: range.error };
        }
        const from = range.finished_from || '';
        const to = range.finished_to || '';
        return {
            finishedFrom: from,
            finishedTo: to,
            period: { from, to },
            error: null,
        };
    }

    const range = jobsDateRangeForStatsApi(customFrom, customTo);
    if (range.error) {
        return { error: range.error };
    }
    const from = range.finished_from || '';
    const to = range.finished_to || '';
    return {
        finishedFrom: from,
        finishedTo: to,
        period: { from, to },
        error: null,
    };
}

/** Período padrão do painel: primeiro dia do mês até hoje. */
export function defaultPrintersOverviewPeriod() {
    const today = new Date();
    const start = startOfMonth(today);
    return {
        from: format(start, 'yyyy-MM-dd'),
        to: format(today, 'yyyy-MM-dd'),
    };
}

function maintenanceDay(occurredAt) {
    if (!occurredAt) {
        return '';
    }
    return String(occurredAt).trim().slice(0, 10);
}

export function isMaintenanceInPeriod(occurredAt, from, to) {
    const day = maintenanceDay(occurredAt);
    if (!day) {
        return false;
    }
    if (!from || !to) {
        return true;
    }
    return day >= from && day <= to;
}

/** Contagem, tempo e peças de manutenção por impressora no período. */
export function summarizeMaintenancesByPrinter(maintenances, from, to) {
    const summary = {};
    (maintenances || []).forEach((row) => {
        const printerId = row.printer_uuid;
        if (!printerId || !isMaintenanceInPeriod(row.occurred_at, from, to)) {
            return;
        }
        if (!summary[printerId]) {
            summary[printerId] = {
                count: 0,
                downtimeMinutes: 0,
                partsCount: 0,
                partsTotalCents: 0,
            };
        }
        summary[printerId].count += 1;
        const analysis = parseInt(row.analysis_duration_minutes, 10) || 0;
        const correction = parseInt(row.correction_duration_minutes, 10) || 0;
        summary[printerId].downtimeMinutes += analysis + correction;
        summary[printerId].partsCount += replacementPartsCount(row.replacement_parts);
        summary[printerId].partsTotalCents += maintenancePartsTotalCents(row.replacement_parts);
    });
    return summary;
}

/** Duração literal: 45min, 1h30min, 106h40min (sem decimais). */
export function formatMaintenanceDurationLiteral(totalMinutes) {
    const minutes = Math.max(0, Math.round(Number(totalMinutes)) || 0);
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    if (hours === 0) {
        return `${remainder}min`;
    }
    if (remainder === 0) {
        return `${hours}h`;
    }
    return `${hours}h${remainder}min`;
}

export const MAINTENANCE_DOWNTIME_SUFFIX = ' do seu tempo em manutenção';

/** Texto plano (análise + correção), ex.: "1h30min do seu tempo em manutenção". */
export function formatMaintenanceDowntimeLabel(totalMinutes) {
    if (!totalMinutes || totalMinutes <= 0) {
        return '--';
    }
    return `${formatMaintenanceDurationLiteral(totalMinutes)}${MAINTENANCE_DOWNTIME_SUFFIX}`;
}

export function printersInProgressSet(jobs) {
    const set = new Set();
    (jobs || []).forEach((job) => {
        if (job.status === 'em_andamento' && job.printer_uuid) {
            set.add(job.printer_uuid);
        }
    });
    return set;
}

/** Um card/linha por impressora, com métricas do período e dados cadastrais. */
export function buildPrinterOverviewCards(printers, stats, maintenanceSummary, inProgressSet) {
    const statsMap = Object.fromEntries((stats || []).map((item) => [item.printer_uuid, item]));

    return sortPrintersByName(printers || []).map((printer) => {
        const maintenance = maintenanceSummary[printer.id] || {
            count: 0,
            downtimeMinutes: 0,
            partsCount: 0,
            partsTotalCents: 0,
        };
        const stat = statsMap[printer.id];
        return {
            id: printer.id,
            printer_uuid: printer.id,
            name: printer.name,
            brand: printer.brand,
            model: printer.model,
            snapshot_url: printer.snapshot_url,
            active: printer.active !== false,
            avg_consumption_watts: printer.avg_consumption_watts,
            consumption: printer.consumption ?? printer.power_consumption_watts,
            price: printer.price ?? printer.acquisition_value,
            total_jobs: stat?.total_jobs ?? 0,
            success_rate: stat?.success_rate ?? 0,
            total_duration_minutes: stat?.total_duration_minutes ?? 0,
            total_filament_grams: stat?.total_filament_grams ?? 0,
            maintenance_count: maintenance.count,
            maintenance_downtime_minutes: maintenance.downtimeMinutes,
            parts_count: maintenance.partsCount,
            parts_total_cents: maintenance.partsTotalCents,
            is_printing: inProgressSet.has(printer.id),
            filaments: stat?.filaments || printer.filaments || [],
        };
    });
}

export { buildPrinterStatsQuery } from './jobs';

export function buildPrinterOverviewJobsUrl(printerUuid, period) {
    const params = new URLSearchParams();
    if (printerUuid) {
        params.set('printer_uuid', printerUuid);
    }
    if (period?.from) {
        params.set('finished_from', period.from);
    }
    if (period?.to) {
        params.set('finished_to', period.to);
    }
    return `/admin/jobs?${params.toString()}`;
}

export function buildPrinterOverviewMaintenancesUrl(printerUuid) {
    const params = new URLSearchParams();
    if (printerUuid) {
        params.set('printer_uuid', printerUuid);
    }
    return `/admin/printer-maintenances?${params.toString()}`;
}
